import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Space } from 'antd';
import { EyeOutlined, MailOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';
import { HAB, can } from '../variables/constants';
import { partieGarantie } from '../services/partieGarantie';
import DemandeGarantieCourrielModal from './DemandeGarantieCourrielModal';

const STATUTS_DEMANDE = ['GARANTIE_DEMANDEE', 'GARANTIE_ATTENTE_CONFIRMATION'];

/**
 * Où en est le courriel de demande de confirmation de garantie.
 *
 * Il part tout seul à l'entrée en demande (bureau homologue pour un ET, compagnie
 * togolaise pour un TE). L'agent doit pouvoir le constater - à qui, quand - et, s'il
 * n'est pas parti (adresse absente ou provisoire, serveur indisponible), savoir
 * pourquoi et le renvoyer. Sans ce bandeau, un échec d'envoi resterait invisible :
 * le statut dit « demandée » que le courriel soit parti ou non.
 *
 * Le courriel lui-même se relit depuis le bandeau, et se corrige avant l'envoi :
 * adresse (quand celle du référentiel est provisoire), objet, message.
 */
export default function DemandeGarantieBandeau({ sinistre }) {
  const { t } = useTranslation('commun');
  const [etat, setEtat] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [courriel, setCourriel] = useState(false);
  const visible = !!sinistre && STATUTS_DEMANDE.includes(sinistre.statut) && !sinistre.repriseHistorique;
  const id = sinistre?.sinistreTrackingId;

  const charger = useCallback(async () => {
    if (!visible) { setEtat(null); return; }
    try {
      const res = await axiosInstance.get(`/v1/sinistres/${id}/demande-garantie`, { skipErrorToast: true });
      setEtat(res.data?.data || null);
    } catch {
      setEtat(null);
    }
  }, [id, visible]);

  // Le statut change quand la demande est faite : le bandeau se recharge avec lui.
  useEffect(() => { charger(); }, [charger, sinistre?.statut]);

  if (!visible || !etat) return null;

  // « à la compagnie SUNU… » / « au bureau homologue BNCB CI » : le rôle d'abord, le nom ensuite.
  const vers = `${partieGarantie(sinistre).a} ${etat.destinataire || ''}`.trim();

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const res = await axiosInstance.post(`/v1/sinistres/${id}/demande-garantie/envoyer`);
      const message = res.data?.message || t('demandeGarantieBandeau.demandeEnvoyee');
      // Le serveur garde le courrier même quand le courriel ne part pas, et le dit.
      if (/NON envoyé/i.test(message)) toast.warning(message); else toast.success(message);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('demandeGarantieBandeau.echecEnvoi'));
    } finally {
      setEnvoi(false);
      charger();
    }
  };

  const peutEnvoyer = can(HAB.SINISTRE_GARANTIE) && !etat.motifAdresse;
  const envoiRapide = (libelle, confirmer) => (peutEnvoyer ? (
    confirmer ? (
      <Popconfirm title={t('demandeGarantieBandeau.renvoyerDemande')} description={t('demandeGarantieBandeau.nouveauCourriel', { email: etat.email })}
        onConfirm={envoyer} okText={t('demandeGarantieBandeau.renvoyer')} cancelText={t('demandeGarantieBandeau.annuler')}>
        <Button size="small" loading={envoi}>{libelle}</Button>
      </Popconfirm>
    ) : (
      <Button size="small" type="primary" loading={envoi} onClick={envoyer}
        style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>{libelle}</Button>
    )
  ) : null);

  // Voir le courriel : toujours ; le corriger et l'envoyer : avec le droit de gérer la garantie.
  const voir = (libelle, principal) => (
    <Button size="small" icon={<EyeOutlined />} onClick={() => setCourriel(true)}
      type={principal ? 'primary' : 'default'}
      style={principal ? { background: '#1a6b3c', borderColor: '#1a6b3c' } : undefined}>
      {libelle}
    </Button>
  );
  const actions = (...boutons) => <Space size={8} wrap>{boutons.filter(Boolean)}</Space>;
  const fenetre = (
    <DemandeGarantieCourrielModal open={courriel} onClose={() => setCourriel(false)}
      sinistre={sinistre} onEnvoye={charger} />
  );
  const corriger = can(HAB.SINISTRE_GARANTIE);

  const style = { borderRadius: 12, margin: '16px 0' };

  if (etat.statutEnvoi === 'ENVOYE') {
    return (
      <>
        <Alert showIcon type="success" icon={<MailOutlined />} style={style}
          message={t('demandeGarantieBandeau.envoyee', { vers })}
          description={[
            etat.email,
            etat.dateEnvoi ? t('demandeGarantieBandeau.le', { date: dayjs(etat.dateEnvoi).format(t('demandeGarantieBandeau.formatDateHeure')) }) : null,
            etat.reference ? t('demandeGarantieBandeau.reference', { reference: etat.reference }) : null,
            t('demandeGarantieBandeau.dansEnvoyes'),
          ].filter(Boolean).join(' · ')}
          action={actions(voir(t('demandeGarantieBandeau.voirCourriel'), false), envoiRapide(t('demandeGarantieBandeau.renvoyer'), true))}
        />
        {fenetre}
      </>
    );
  }

  if (etat.statutEnvoi === 'ECHEC') {
    return (
      <>
        <Alert showIcon type="warning" icon={<MailOutlined />} style={style}
          message={t('demandeGarantieBandeau.pasPartie', { vers })}
          description={etat.motifAdresse
            || t('demandeGarantieBandeau.serveurARefuse', { email: etat.email, reference: etat.reference || '' })}
          action={actions(voir(corriger ? t('demandeGarantieBandeau.voirEtCorriger') : t('demandeGarantieBandeau.voirCourriel'), !peutEnvoyer && corriger),
            envoiRapide(t('demandeGarantieBandeau.renvoyer'), false))}
        />
        {fenetre}
      </>
    );
  }

  return (
    <>
      <Alert showIcon type={etat.motifAdresse ? 'warning' : 'info'} icon={<MailOutlined />} style={style}
        message={t('demandeGarantieBandeau.nonEnvoyee', { vers })}
        description={etat.motifAdresse
          || (etat.envoiAutomatiqueActif
            ? t('demandeGarantieBandeau.aucunCourriel', { vers, email: etat.email })
            : t('demandeGarantieBandeau.envoiSuspendu', { vers, email: etat.email }))}
        action={actions(
          // Adresse provisoire : le seul chemin est de relire le courriel et d'y saisir la vraie adresse.
          voir(corriger ? (etat.motifAdresse ? t('demandeGarantieBandeau.voirEtEnvoyer') : t('demandeGarantieBandeau.voirEtModifier')) : t('demandeGarantieBandeau.voirCourriel'),
            !peutEnvoyer && corriger),
          envoiRapide(t('demandeGarantieBandeau.envoyerDemande'), false))}
      />
      {fenetre}
    </>
  );
}
